import { Routes } from 'types/routeTypes';
import { routeIsStructural } from 'utils/baseHelpers';
import {
  extendUserSession,
  getCurrentAuthToken,
  isTokenExpired,
} from 'utils/authHelpers';

/** The one renewal on its way, shared by every request that needs it. */
let renewing: Promise<string | null> | null = null;

const freshToken = (): string | null => {
  const token = getCurrentAuthToken('preserve');
  return token && !isTokenExpired(token) ? token : null;
};

/**
 * SYM-6752. The token a request may be sent with, or null when there is none.
 *
 * A token that expired (a laptop that slept past its expiry) used to be
 * deleted on the spot and the request sent as "Bearer null". The server
 * refuses that with 400 on every address, so the screen went on failing
 * without ever reaching the login page (Mark's QA test, 2 Nov 2026). Now an
 * expired or missing token is renewed once; if that fails, null.
 */
export const usableAuthToken = async (): Promise<string | null> => {
  const token = freshToken();
  if (token) return token;

  if (!renewing) {
    renewing = extendUserSession()
      .then(freshToken)
      .catch(() => null)
      .finally(() => {
        renewing = null;
      });
  }
  return renewing;
};

/**
 * The Authorization header for a request built by hand (fetch, axios), with
 * the same rule as the data requests: a valid token, renewed once if needed.
 * Without one: no header at all, and the person signs in again.
 */
export const authHeader = async (): Promise<{ Authorization?: string }> => {
  const token = await usableAuthToken();
  if (!token) {
    sendToLogin();
    return {};
  }
  return { Authorization: `Bearer ${token}` };
};

/** No usable token: the person signs in again, as the plain HTTP path does. */
export const sendToLogin = (): void => {
  localStorage.removeItem('token');
  if (!routeIsStructural(window.location.pathname)) {
    window.location.replace(Routes.LOGIN);
  }
};
